import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useUpdateRelation } from '@/api/hooks/useUpdateRelation'
import { RelationFieldGroup } from '@/components/molecules/relation-field-group'
import { type Relation } from '@/components/molecules/relation-select'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { IndividualType } from '@/hooks/useIndividuals'

type Owner = NonNullable<IndividualType['users']>[number]

type Props = {
  individualId: string
  onOpenChange(open: boolean): void
  open: boolean
  owner: Owner
}

/**
 * Admin-only editing of *another* user's relation to the individual. The
 * endpoint targets the current user unless `userId` is supplied, and the
 * backend rejects the cross-user case for non-admins.
 */
export const EditOwnerRelationDialog = ({ individualId, onOpenChange, open, owner }: Props) => {
  const { t } = useTranslation()
  const { mutateAsync: updateRelation } = useUpdateRelation()

  const [relation, setRelation] = useState<Relation>(owner.relation)
  const [relationOther, setRelationOther] = useState(owner.relationOther ?? '')
  const [relationOtherError, setRelationOtherError] = useState<string>()
  const [isGuardian, setIsGuardian] = useState(owner.isGuardian)
  const [isLegalRepresentative, setIsLegalRepresentative] = useState(owner.isLegalRepresentative)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reset to the latest values whenever the dialog (re)opens.
  useEffect(() => {
    if (open) {
      setRelation(owner.relation)
      setRelationOther(owner.relationOther ?? '')
      setRelationOtherError(undefined)
      setIsGuardian(owner.isGuardian)
      setIsLegalRepresentative(owner.isLegalRepresentative)
    }
  }, [open, owner])

  const handleSubmit = async () => {
    if (relation === 'other' && !relationOther.trim()) {
      setRelationOtherError(t('formValidation.relationOtherRequired'))
      return
    }

    setIsSubmitting(true)

    try {
      await updateRelation({
        individualId,
        requestBody: {
          isGuardian,
          isLegalRepresentative,
          relation,
          relationOther: relation === 'other' ? relationOther : undefined,
          userId: owner.id,
        },
      })
      onOpenChange(false)
    } catch (error) {
      toast.error((error as Error).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const ownerLabel = owner.name?.trim() || `${owner.pid}-xxxx`

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('editRelationDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('editRelationDialog.description', { name: ownerLabel })}
          </DialogDescription>
        </DialogHeader>

        <RelationFieldGroup
          idPrefix={`edit-relation-${owner.id}`}
          includeOther
          isGuardian={isGuardian}
          isLegalRepresentative={isLegalRepresentative}
          onIsGuardianChange={setIsGuardian}
          onIsLegalRepresentativeChange={setIsLegalRepresentative}
          onRelationChange={setRelation}
          onRelationOtherChange={value => {
            setRelationOther(value)
            setRelationOtherError(undefined)
          }}
          perspective="third-person"
          relation={relation}
          relationLabel={t('relation.editLabel')}
          relationOther={relationOther}
          relationOtherError={relationOtherError}
        />

        <div className="flex justify-end gap-2">
          <Button onClick={() => onOpenChange(false)} type="button" variant="outline">
            {t('global.cancel')}
          </Button>
          <Button isLoading={isSubmitting} onClick={handleSubmit} type="button">
            {t('global.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default EditOwnerRelationDialog
